import { useState, FormEvent, ChangeEvent, useEffect, useRef } from 'react';
import cloud4medicalLogo from '../../assets/img/logo/cloud4medical-logo-white.webp';
import logopadLogo from '../../assets/img/logo/logopad_logo_white.svg';
import psychopadLogo from '../../assets/img/logo/psychopad_logo_white.svg';
import fyziopadLogo from '../../assets/img/logo/fyziopad_logo_white.svg';
import { useTranslation } from 'react-i18next';

type FormData = {
    name: string;
    email: string;
    phone: string;
    message: string;
    language: string;
};

type Status = {
    success: boolean;
    message: string;
} | null;

const Footer = () => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        phone: '',
        message: '',
        language: i18n.language,
    });

    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState<Status>(null);
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    const [turnstileReady, setTurnstileReady] = useState(false);
    const turnstileRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const isDev = import.meta.env.VITE_APP_TYPE === 'dev';

    useEffect(() => {
        if (!isDev) {
            const renderTurnstile = () => {
                if (turnstileRef.current && (window as any).turnstile) {
                    widgetIdRef.current = (window as any).turnstile.render(turnstileRef.current, {
                        sitekey: siteKey,
                    });
                    setTurnstileReady(true);
                } else {
                    setTimeout(renderTurnstile, 200);
                }
            };
            renderTurnstile();
        } else {
            setTurnstileReady(true);
        }
    }, [isDev, siteKey]);

    useEffect(() => {
        if (!isDev) {
            const timeout = setTimeout(() => {
                if (!turnstileReady) {
                    setStatus({
                        success: false,
                        message: t('contact.form.errors.turnstileNotLoaded'),
                    });
                }
            }, 7000);

            return () => clearTimeout(timeout);
        }
    }, [turnstileReady, t, isDev]);

    useEffect(() => {
        setFormData(prev => ({ ...prev, language: i18n.language }));
    }, [i18n.language]);

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrorFields((prev) => prev.filter((field) => field !== name));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setStatus(null);
        setErrorFields([]);

        if (submitting) return;

        if (!isDev && !turnstileReady) {
            setStatus({
                success: false,
                message: t('contact.form.errors.turnstileNotReady'),
            });
            setSubmitting(false);
            return;
        }

        const newErrors: string[] = [];

        if (!isDev) {
            const namePattern = /^[A-Za-zÀ-ſ\s]{3,}$/u;
            if (!namePattern.test(formData.name.trim())) newErrors.push('name');

            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(formData.email.trim())) newErrors.push('email');

            const phoneCleaned = formData.phone.replace(/[\s\-]/g, '');
            const phonePattern = /^\+?\d{9,}$/;
            if (formData.phone.trim() && !phonePattern.test(phoneCleaned)) {
                newErrors.push('phone');
            }

            if (formData.message.trim().length < 10) newErrors.push('message');
        } else {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailPattern.test(formData.email.trim())) newErrors.push('email');
        }

        if (newErrors.length > 0) {
            setErrorFields(newErrors);
            setStatus({ success: false, message: t('contact.form.errors.checkFields') });
            return;
        }

        setSubmitting(true);

        let token = '';
        if (!isDev) {
            const turnstile = (window as any).turnstile;
            if (!turnstile || typeof turnstile.getResponse !== 'function') {
                setStatus({
                    success: false,
                    message: t('contact.form.errors.turnstileFailed'),
                });
                setSubmitting(false);
                return;
            }

            token = turnstile.getResponse(widgetIdRef.current);
            if (!token) {
                turnstile.reset(widgetIdRef.current);
                setStatus({
                    success: false,
                    message: t('contact.form.errors.confirmVerification'),
                });
                setSubmitting(false);
                return;
            }
        }

        try {
            const res = await fetch('/api/send-mail.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    ...formData, 
                    'cf-turnstile-response': token,
                }),
            });

            const data = await res.json();
            setStatus(data);
            setSubmitting(false);

            if (data.success) {
                setFormData({ name: '', email: '', phone: '', message: '', language: i18n.language });
                setErrorFields([]);
                if (!isDev && (window as any).turnstile) {
                    (window as any).turnstile.reset(widgetIdRef.current);
                }
            }
        } catch (err) {
            setStatus({
                success: false,
                message: t('contact.form.errors.sendFailed'),
            });
            setSubmitting(false);
        }
    };

    return (
        <footer className="bg-[#0F2D50] text-[#c5b8e6]" id="kontakt">
            <div className="py-20 md:py-50 max-w-[1120px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 md:gap-50 px-6">
                <div className="lg:max-w-[460px]">
                    <h2 className="text-5xl font-extrabold mb-4 text-white">{t('contact.title')}</h2>
                    <p className="text-lg mb-4">
                        {t('contact.description')}
                        <span className="font-bold block text-white">
                            {t('contact.freeVisit')}
                        </span>
                    </p>
                    <p>
                        {t('contact.formDescription')}
                    </p>
                    <p className="mt-4">
                        {t('contact.orCall')}{' '}
                        <a href="tel:+420602257588" className="font-bold text-white no-underline">
                            +420 602 257 588
                        </a>
                    </p>
                    <div className="relative mt-8 p-6 rounded-xl bg-white/5 border border-white/10 text-left text-sm text-[#c5b8e6] shadow-sm">
                        <img
                            src={cloud4medicalLogo}
                            alt={t('contact.company.logoAlt')}
                            className="mx-auto py-3 sm:py-0 w-[200px] sm:absolute top-6 right-6 sm:w-[160px] opacity-80"
                        />
                        <h3 className="text-white font-semibold text-base mb-2">{t('contact.company.name')}</h3>
                        <p className="leading-relaxed space-y-1">
                            <span className="block">
                                <strong className="text-white">{t('contact.company.vat')}:</strong> 08771243
                            </span>
                            <span className="block">{t('contact.company.address')}</span>
                            <span className="block">{t('contact.company.city')}</span>
                            <span className="block">74764</span>
                        </p>
                        <p className="mt-4">
                            {t('contact.company.registration')}{' '}
                            <strong className="text-white">C 80756/KSOS</strong> {t('contact.company.registrationEnd')}
                        </p>
                    </div>
                </div>
                <div>
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="mb-4">
                            <input
                                name="name"
                                type="text"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={t('contact.form.name')}
                                className={`bg-white w-full min-h-[70px] p-5 px-7.5 border rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary ${errorFields.includes('name') ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errorFields.includes('name') && (
                                <p className="text-red-500 text-sm mt-1">{t('contact.form.errors.invalidName')}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <input
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder={t('contact.form.email')}
                                className={`bg-white w-full min-h-[70px] p-5 px-7.5 border rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary ${errorFields.includes('email') ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errorFields.includes('email') && (
                                <p className="text-red-500 text-sm mt-1">{t('contact.form.errors.invalidEmail')}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <input
                                name="phone"
                                type="tel"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder={t('contact.form.phone')}
                                className={`bg-white w-full min-h-[70px] p-5 px-7.5 border rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary ${errorFields.includes('phone') ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errorFields.includes('phone') && (
                                <p className="text-red-500 text-sm mt-1">{t('contact.form.errors.invalidPhone')}</p>
                            )}
                        </div>

                        <div className="mb-4">
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder={t('contact.form.message')}
                                rows={4}
                                className={`bg-white w-full min-h-[180px] p-6.5 px-7.5 border rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary max-h-192 ${errorFields.includes('message') ? 'border-red-500' : 'border-gray-300'
                                    }`}
                            />
                            {errorFields.includes('message') && (
                                <p className="text-red-500 text-sm mt-1">{t('contact.form.errors.messageTooShort')}</p>
                            )}
                        </div>

                        {!isDev && <div ref={turnstileRef} className="mb-4"></div>}

                        {status && (
                            <p className={`text-sm mb-4 ${status.success ? 'text-green-500' : 'text-red-500'}`}>
                                {status.message}
                            </p>
                        )}

                        <button
                            type="submit"
                            className="w-full"
                            disabled={submitting}
                        >
                            {submitting ? t('contact.form.sending') : t('contact.form.send')}
                        </button>

                    </form>
                </div>
            </div>

            <div className="mt-12 p-7.5 text-[#c5b8e6] text-sm border-t border-gray-600 bg-[#061c41]">
                <div id="3-logos" className="flex flex-col md:flex-row items-center justify-center gap-15 py-20 border-b border-gray-600">
                    <a 
                        href="https://logo.pad.cz" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                        <img 
                            src={logopadLogo} 
                            alt={t('contact.company.logoAlt')} 
                            className="h-[40px] w-auto"
                        />
                    </a>
                    <a 
                        href="https://psycho.pad.cz" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                        <img 
                            src={psychopadLogo} 
                            alt={t('contact.company.logoAlt')} 
                            className="h-[40px] w-auto"
                        />
                    </a>
                    <a 
                        href="https://fyzio.pad.cz" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                        <img 
                            src={fyziopadLogo} 
                            alt={t('contact.company.logoAlt')} 
                            className="h-[40px] w-auto"
                        />
                    </a>
                </div>
                <div
                    className="flex flex-col-reverse md:flex-row gap-y-15 justify-between text-center mx-auto pt-7.5"
                    style={{ maxWidth: 'var(--width-container)' }}
                >
                    <p>{t('footer.copyright')}</p>
                    <div className="flex justify-center gap-8 mt-2">
                        <a
                            href="https://logo.pad.cz/podporovatele"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#c5b8e6] no-underline hover:text-white transition"
                        >
                            {t('footer.supporters')}
                        </a>
                        <a
                            href="https://logo.pad.cz/memorandum"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#c5b8e6] no-underline hover:text-white transition"
                        >
                            {t('footer.privacyPolicy')}
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
